import { useRef } from "react";
import { useQuery, type QueryFunction } from "@tanstack/react-query";
import { authQueryOptions } from "@scroll/shared-query";
import type { User } from "@scroll/shared-types";

export interface AuthCoreConfig {
  isSignedIn: boolean | undefined;
  isLoaded: boolean;
  clerkUserId?: string;
  clerkUsername?: string | null;
  clerkFullName?: string | null;
  clerkImageUrl?: string | null;
  clerkEmail?: string | null;
  queryFn: QueryFunction<User | null>;
}

export interface AuthCoreResult {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  dbUser: User | null | undefined;
}

export function useAuthCore(config: AuthCoreConfig): AuthCoreResult {
  const { isSignedIn, isLoaded, clerkUserId, clerkUsername, clerkFullName, clerkImageUrl, clerkEmail, queryFn } = config;
  const hasResolved = useRef(false);

  const { data: dbUser, isLoading: isDbLoading } = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
    queryFn,
    enabled: isSignedIn === true,
    ...authQueryOptions,
  });

  // Once auth has fully resolved, never flicker isLoading back to true.
  // This prevents unmounting the app during Clerk token refreshes.
  const rawLoading = !isLoaded || (isSignedIn === true && isDbLoading);
  if (!rawLoading && isLoaded) {
    hasResolved.current = true;
  }
  const isLoading = hasResolved.current ? false : rawLoading;

  // Combine Clerk user with database user
  const user: User | null = dbUser
    ? {
        ...dbUser,
        profileImageUrl: dbUser.profileImageUrl || clerkImageUrl || null,
        displayName: dbUser.displayName || clerkFullName || null,
      }
    : isSignedIn && clerkUserId
      ? {
          id: clerkUserId,
          username: clerkUsername ?? null,
          displayName: clerkFullName || null,
          profileImageUrl: clerkImageUrl || null,
          bio: null,
          email: clerkEmail || null,
        } as User
      : null;

  return {
    user,
    isLoading,
    isAuthenticated: isSignedIn === true,
    dbUser,
  };
}
